import React, { useEffect, useState } from 'react';
import { API_URLS } from '../apiUrl';
import PropertyTypeDropdown from '../pages/customHooks/propertyTypeDropdown';
import PropertyTypeDescriptionDropdown from '../pages/customHooks/propertyTypeDescriptionDropdown';
import UseOfPropertyDropdown from '../pages/customHooks/useOfPropertyDropdown';
import axiosInstance from '../components/axiosInstance';

const PurposeOfPropertyEdit = ({ isOpen, closeModal, purposeOfPropertyName, purposeOfPropertyId, propertyTypeId, propertyTypeDescriptionId, useOfPropertyId, useOfPropertyName, propertyTypeName }: any) => {
    
    const [formData, setFormData] = useState<any>({
        purpose_of_use: "",
        property_type_id: 0,
        property_description_id: "",
        use_of_property_id: 0,
        pourpose_primary_id: purposeOfPropertyId
    });

    // Effect to update formData when the modal opens or props change
    useEffect(() => {
        if (isOpen) {
            setFormData((prevState: any) => ({
                ...prevState,
                pourpose_primary_id: purposeOfPropertyId,
                purpose_of_use: purposeOfPropertyName || "",
                property_type_id: propertyTypeId || 0,
                property_description_id: propertyTypeDescriptionId || "",
                use_of_property_id: useOfPropertyId || 0,
            }));
        }
    }, [isOpen, purposeOfPropertyId, purposeOfPropertyName, propertyTypeId, propertyTypeDescriptionId, useOfPropertyId]);

    const [errors, setErrors] = useState<any>({});
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [dataExistMessage, setDataExistMessage] = useState<string>("");

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | any
    ) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
    
        if (!formData.purpose_of_use.trim()) {
            newErrors.description_name = "Purpose Of Property is required";
        } else if (!/^[A-Za-z\s]+$/.test(formData.purpose_of_use)) {
            newErrors.description_name = "Only text and spaces are allowed";
        }
    
        setErrors(newErrors);
    
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const formDataToSubmit = {
            ...formData,
            property_type_id: formData.property_type_id || propertyTypeId,
            property_description_id: formData.property_description_id || propertyTypeDescriptionId,
            use_of_property_id: formData.use_of_property_id || useOfPropertyId,
        };

        if (validateForm()) {
            try {
                const response = await axiosInstance.put(`${API_URLS.PurposeOfPropertyUpdate}`, formDataToSubmit);

                if (response.data.message) {
                   
                    setSuccessMessage(`${response.data.message}`);
                    setTimeout(() => {
                        setSuccessMessage("");
                        closeModal();
                    }, 5000);
                }

                setErrors({});
            } catch (error) {
                setDataExistMessage("Purpose of Property already exists. Please choose a different name.");
                setTimeout(() => {
                    setDataExistMessage("");
                    closeModal();
                }, 5000);
            }
        }
    };

    const [handleDropdownToggle, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();
    const [, isDropdownOpenPropertyDescription, , propertyDescriptions, loadingPropertyDescription, setIsDropdownOpenPropertyDescription] = PropertyTypeDescriptionDropdown(formData.property_type_id);
    const [setIsDropdownOpenUseOfProperty, isDropdownOpenUseOfProperty, , useOfProperty, loadingUseOfProperty] = UseOfPropertyDropdown(formData.property_type_id, formData.property_description_id);

    const handleOutsideClick = (e: any) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    const handlePropertyTypeDescriptionSelect = (e: any) => {
        const { value, checked } = e.target;

        setFormData((prevFormData: any) => {
            let newSelections = formData.property_description_id ? formData.property_description_id.split(",") : [];

            if (checked) {
                newSelections.push(value);
            } else {
                const index = newSelections.indexOf(value);
                if (index > -1) {
                    newSelections.splice(index, 1);
                }
            }

            return {
                ...prevFormData,
                property_description_id: newSelections.join(","),
            };
        });
    };

    const handlePropertyTypeSelect = (selectedValue: number) => {
        setFormData((prevState: any) => ({
            ...prevState,
            property_type_id: selectedValue,
        }));
        setIsDropdownOpen(false);
    };

    const handlePropertyTypeSelectUseOfProperty = (selectedValue: number) => {
        setFormData((prevState: any) => ({
            ...prevState,
            use_of_property_id: selectedValue,
        }));
        setIsDropdownOpenUseOfProperty(false);
    };

    const selectedPropertyTypeName = propertyTypes.find((property: any) => property.type_primary_id === formData.property_type_id)?.type_name || propertyTypeName;
    
    const selectedPropertyDescriptions = propertyDescriptions
        .filter((property: any) => formData.property_description_id.split(",").includes(String(property.id)))
        .map((property: any) => property.type_description_name)
        .join(", ");

    const selectedUseOfProperty = useOfProperty
        .find((property: any) => property.useproperty_primary_id === formData.use_of_property_id)
        ?.use_of_property || useOfPropertyName;

    const useOfPropertyDropdownToggle = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDropdownOpenUseOfProperty((prev: any) => !prev);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center" onClick={handleOutsideClick}>
            <div className="bg-white p-6 rounded-lg shadow-lg w-[740px] relative" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">Edit Purpose Of Property</h2>
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                        {/* Property Type Dropdown */}
                        <div className="relative z-[4]">
                            <label htmlFor="property_type_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                Property Type <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    onClick={handleDropdownToggle}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                                    type="button"
                                >
                                    {selectedPropertyTypeName || "Select Property Type"}
                                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                                </button>

                                {isDropdownOpen && (
                                    <select
                                        id="property_type_id"
                                        name="property_type_id"
                                        value={formData.property_type_id}
                                        onChange={(e) => handlePropertyTypeSelect(Number(e.target.value))}
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full"
                                        size={10}
                                    >
                                        <option value={0} disabled>Select Property Type</option>
                                        {propertyTypes.map((property: any) => (
                                            <option key={property.type_primary_id} value={property.type_primary_id}>
                                                {property.type_name}
                                            </option>
                                        ))}
                                        {loading && <option>Loading...</option>}
                                    </select>
                                )}
                            </div>
                            {errors.property_type_id && <p className="text-sm text-red-600 mt-1">{errors.property_type_id}</p>}
                        </div>

                        {/* Property Type Description Dropdown */}
                        <div>
                            <label htmlFor="property_description_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                Property Type Description <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    onClick={() => setIsDropdownOpenPropertyDescription((prev: any) => !prev)}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                                    type="button"
                                >
                                    {selectedPropertyDescriptions || "Select Property Type Description"}
                                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                                </button>

                                {isDropdownOpenPropertyDescription && (
                                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[175px] overflow-y-auto">
                                        {propertyDescriptions.map((property: any) => (
                                            <div key={property.id} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id={`property_${property.id}`}
                                                    value={property.id}
                                                    checked={formData.property_description_id.split(",").includes(String(property.id))}
                                                    onChange={handlePropertyTypeDescriptionSelect}
                                                    className="text-blue-600"
                                                />
                                                <label htmlFor={`property_${property.id}`} className="text-sm text-gray-900 dark:text-white">
                                                    {property.type_description_name}
                                                </label>
                                            </div>
                                        ))}
                                        {loadingPropertyDescription && <p>Loading...</p>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Use Of Property Dropdown */}
                        <div>
                            <label htmlFor="use_of_property_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                Use Of The Property <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    onClick={useOfPropertyDropdownToggle}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                                    type="button"
                                >
                                    {selectedUseOfProperty || "Select Use Of Property"}
                                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                                </button>

                                {isDropdownOpenUseOfProperty && (
                                    <select
                                        id="use_of_property_id"
                                        name="use_of_property_id"
                                        value={formData.use_of_property_id}
                                        onChange={(e) => handlePropertyTypeSelectUseOfProperty(Number(e.target.value))}
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 absolute top-full"
                                        size={10}
                                    >
                                        <option value={0} disabled>Select Use Of Property</option>
                                        {useOfProperty.map((property: any) => (
                                            <option key={property.useproperty_primary_id} value={property.useproperty_primary_id}>
                                                {property.use_of_property}
                                            </option>
                                        ))}
                                        {loadingUseOfProperty && <option>Loading...</option>}
                                    </select>
                                )}
                            </div>
                            {errors.use_of_property_id && <p className="text-sm text-red-600 mt-1">{errors.use_of_property_id}</p>}
                        </div>

                        {/* Purpose Of Property */}
                        <div>
                            <label htmlFor="purpose_of_use" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                Purpose Of Property <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                id="purpose_of_use"
                                name="purpose_of_use"
                                value={formData.purpose_of_use}
                                onChange={handleChange}
                                className="block w-full p-2.5 bg-gray-50 border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                            />

                            {errors.description_name && (
                                <p className="text-sm text-red-600 mt-1">{errors.description_name}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-between gap-5 mt-4">
                        <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg">Update</button>
                    </div>
                </form>

                {successMessage && (
                    <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
                        <span>{successMessage}</span>
                        <button
                            onClick={() => setSuccessMessage('')}
                            className="ml-4 text-white font-bold text-lg focus:outline-none"
                        >
                            &times;
                        </button>
                    </div>
                )}

                {dataExistMessage && (
                    <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
                        <span>{dataExistMessage}</span>
                        <button
                            onClick={() => setDataExistMessage('')}
                            className="ml-4 text-white font-bold text-lg focus:outline-none"
                        >
                            &times;
                        </button>
                    </div>
                )}

                <button
                    onClick={closeModal}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                >
                    <i className="ri-close-line"></i>
                </button>
            </div>
        </div>
    );
};

export default PurposeOfPropertyEdit;